const initCustomerCareDrawer = () => {
  const drawerRoot = document.querySelector('[data-customer-care]');

  if (!drawerRoot || drawerRoot.dataset.customerCareInitialized === 'true') {
    return;
  }

  drawerRoot.dataset.customerCareInitialized = 'true';
  const drawer = drawerRoot.querySelector('[data-customer-care-drawer]');
  const closeButtons = drawerRoot.querySelectorAll('[data-customer-care-close]');
  const openButtons = document.querySelectorAll('[data-customer-care-open]');
  const stylesheetReady = import(
    /* webpackChunkName: "tailwind-customer-care" */ '../../tailwind/chunks/customer-care-drawer.css'
  );
  stylesheetReady.then(() => {
    openButtons.forEach((button) => {
      button.disabled = false;
    });
  });
  let returnFocus = null;
  let closeTimer = null;
  let restoreDocumentOverflow = false;

  const getFocusableElements = () =>
    Array.from(
      drawer.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );

  const close = () => {
    drawerRoot.removeAttribute('data-customer-care-state');
    drawerRoot.setAttribute('aria-hidden', 'true');
    if (!restoreDocumentOverflow) {
      document.documentElement.classList.remove('overflow-hidden');
    }
    closeTimer = window.setTimeout(() => {
      drawerRoot.hidden = true;
      closeTimer = null;
    }, 300);
    returnFocus?.focus();
  };

  const open = async (trigger) => {
    returnFocus = trigger;
    await stylesheetReady;
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    drawerRoot.hidden = false;
    drawerRoot.setAttribute('aria-hidden', 'false');
    restoreDocumentOverflow =
      document.documentElement.classList.contains('overflow-hidden');
    document.documentElement.classList.add('overflow-hidden');
    window.requestAnimationFrame(() => {
      drawerRoot.dataset.customerCareState = 'open';
      drawer.focus();
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-customer-care-open]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
    }
  });

  closeButtons.forEach((button) => button.addEventListener('click', close));

  drawer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = getFocusableElements();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
};

const initFooterContactTheme = () => {
  const footer = document.querySelector('.twc-site-footer');

  if (!footer || footer.dataset.contactThemeInitialized === 'true') {
    return;
  }

  footer.dataset.contactThemeInitialized = 'true';

  const getLuminance = (color) => {
    const channels = color.match(/\d+(?:\.\d+)?/g);
    if (!channels || channels.length < 3) return null;
    const [red, green, blue] = channels.map(Number);
    return (red * 299 + green * 587 + blue * 114) / 1000;
  };

  const syncFooterContactTheme = () => {
    const computedStyle = window.getComputedStyle(footer);
    const backgroundLuminance = getLuminance(computedStyle.backgroundColor);
    const textLuminance = getLuminance(computedStyle.color);
    const hasDarkBackground =
      backgroundLuminance !== null && backgroundLuminance < 128;
    const hasLightText = textLuminance !== null && textLuminance >= 180;

    const nextTheme = hasDarkBackground || hasLightText ? 'dark' : 'light';

    if (footer.dataset.footerTheme !== nextTheme) {
      footer.dataset.footerTheme = nextTheme;
    }
  };

  let footerThemeSyncTimers = [];
  const scheduleFooterContactThemeSync = () => {
    footerThemeSyncTimers.forEach(window.clearTimeout);
    footerThemeSyncTimers = [];

    syncFooterContactTheme();
    window.requestAnimationFrame(syncFooterContactTheme);
    [100, 350, 700].forEach((delay) => {
      footerThemeSyncTimers.push(
        window.setTimeout(syncFooterContactTheme, delay)
      );
    });
  };

  scheduleFooterContactThemeSync();

  const footerThemeObserver = new MutationObserver(
    scheduleFooterContactThemeSync
  );
  [document.documentElement, document.body, footer].forEach((element) => {
    footerThemeObserver.observe(element, {
      attributes: true,
    });
  });

  footer.addEventListener('transitionend', scheduleFooterContactThemeSync);

  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener?.('change', scheduleFooterContactThemeSync);
};

const initializeCustomerCare = () => {
  initCustomerCareDrawer();
  initFooterContactTheme();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCustomerCare, {
    once: true,
  });
} else {
  initializeCustomerCare();
}
